import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, FileXls, FilePdf, LockOpen } from "@phosphor-icons/react";
import { C } from "./shared";

export default function ExportMenu({ fmId, modelName }) {
  const [open, setOpen] = useState(false);
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pendingKind, setPendingKind] = useState(null);
  const ref = useRef(null);

  const loadCost = () => api.get(`/fm/models/${fmId}/export/cost`).then((r) => setCost(r.data)).catch(() => {});
  useEffect(() => { if (open) loadCost(); else setPendingKind(null); /* eslint-disable-next-line */ }, [open]);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const download = (kind) => {
    if (cost && cost.mode !== "free") { setPendingKind(kind); return; }
    doDownload(kind);
  };

  const doDownload = async (kind) => {
    setPendingKind(null);
    setBusy(kind);
    try {
      const { data } = await api.get(`/fm/models/${fmId}/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "excel" ? `${modelName}.xlsx` : `${modelName} - summary.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(kind === "excel" ? "Excel model downloaded" : "Summary PDF downloaded");
      window.dispatchEvent(new Event("clinq:credits-changed"));
      loadCost();
    } catch (e) {
      // 402 credit shortage is toasted globally (api.js) with a Buy credits CTA.
      if (e?.response?.status !== 402) toast.error("Export failed — try again");
    }
    setBusy(null);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="fm-export-btn"
        className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-bold"
        style={{ backgroundColor: C.orange, color: "#000" }}>
        <DownloadSimple size={14} weight="bold" /> Export
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl p-3 z-30 shadow-2xl" data-testid="fm-export-menu"
          style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.25)" }}>
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5"
            style={{ color: cost?.mode === "free" ? C.green : C.tealSoft }} data-testid="fm-export-price">
            {cost == null ? "…" : cost.mode === "free"
              ? <><LockOpen size={12} weight="bold" /> Unlocked — free re-downloads this month</>
              : cost.mode === "refresh"
                ? `${cost.refresh_price} credits — new-month refresh`
                : `${cost.price} credits — unlocks Excel + PDF`}
          </div>
          {pendingKind ? (
            <div data-testid="fm-export-confirm">
              <div className="text-sm font-extrabold text-white mb-1.5">
                Use {cost?.mode === "refresh" ? cost?.refresh_price : cost?.price} credits?
              </div>
              <div className="text-[12px] text-white/60 leading-relaxed mb-3">
                {cost?.mode === "refresh"
                  ? <>New month, refreshed numbers — this download costs <span className="font-bold text-white/90">{cost.refresh_price} credits</span>, then re-downloads are free for the rest of the month.</>
                  : <>This unlocks <span className="font-bold text-white/90">Excel + PDF with free re-downloads this month</span>. A new-month refresh is just {cost?.refresh_price ?? 10} credits.</>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => doDownload(pendingKind)} data-testid="fm-export-confirm-btn"
                  className="flex-1 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wider"
                  style={{ backgroundColor: C.orange, color: "#000" }}>
                  Confirm &amp; download
                </button>
                <button onClick={() => setPendingKind(null)} data-testid="fm-export-cancel-btn"
                  className="rounded-md px-3 py-2 text-xs font-bold text-white/60 hover:text-white transition-colors"
                  style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
          <>
          {[
            ["excel", "Excel model (.xlsx)", "Full branded workbook incl. Bank View for lenders", FileXls],
            ["pdf", "One-pager summary (PDF)", "Headline numbers, KPIs, scenarios & CRS score", FilePdf],
          ].map(([kind, title, sub, Icon]) => (
            <button key={kind} onClick={() => download(kind)} disabled={busy !== null}
              data-testid={`fm-export-${kind}`}
              className="w-full text-left rounded-lg px-3 py-2.5 mb-1.5 flex items-start gap-2.5 transition-colors hover:bg-white/5 disabled:opacity-50"
              style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <Icon size={20} style={{ color: C.orange, marginTop: 2, flexShrink: 0 }} weight="duotone" />
              <div>
                <div className="text-sm font-bold text-white">{busy === kind ? "Preparing…" : title}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>
              </div>
            </button>
          ))}
          {cost && cost.mode === "full" && cost.paid_at && (
            <div className="text-[10px] text-white/35 mt-1">
              Assumptions changed materially since your last export ({cost.changes_since_paid} changes ≥ {cost.threshold}) — a fresh export re-charges at the current tier.
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
}
